/**
 * Generic finite state machine — no Phaser or game-specific dependencies.
 *
 * Used as the foundation for all stateful entities: enemies, heroes, launchers, etc.
 * Each entity creates a StatefulRuntime with typed states and a context builder function.
 */

export interface RuntimeStateContext {
  time: number
  delta: number
  stateTime: number
}

export interface RuntimeState<
  TState extends string,
  TContext extends RuntimeStateContext,
> {
  name: TState
  enter?(ctx: TContext): void
  update(ctx: TContext): TState | null
  exit?(ctx: TContext): void
}

export class StatefulRuntime<
  TState extends string,
  TContext extends RuntimeStateContext,
> {
  private readonly stateMap: Map<TState, RuntimeState<TState, TContext>>
  private readonly buildCtx: (
    time: number,
    delta: number,
    stateName: TState,
    stateTime: number,
  ) => TContext
  private readonly enteredCallback:
    | ((state: TState, ctx: TContext) => void)
    | undefined

  private current: TState
  private changedAt = 0
  private lastCtx: TContext | null = null
  private ready = false
  private pending: TState | null = null

  public constructor(
    initialState: TState,
    states: RuntimeState<TState, TContext>[],
    /**
     * Called every time a context object is needed. Receives raw time/delta
     * from the tick, plus the current state name and elapsed state time.
     * The owning entity should capture world-specific info (playerX, scene, etc.)
     * in its own field and read it here.
     */
    buildCtx: (
      time: number,
      delta: number,
      stateName: TState,
      stateTime: number,
    ) => TContext,
    /**
     * Optional hook called after a state's enter() fires. Useful for emitting
     * events or updating external snapshots (e.g. behavior state on a sprite).
     */
    onEntered?: (state: TState, ctx: TContext) => void,
  ) {
    this.stateMap = new Map(states.map((s) => [s.name, s]))
    this.current = initialState
    this.buildCtx = buildCtx
    this.enteredCallback = onEntered
  }

  /** Pre-initialize without running update logic — call from entity.onSpawn(). */
  public boot(time: number): void {
    if (this.ready) return
    this.changedAt = time
    this.doEnter(this.buildCtx(time, 0, this.current, 0))
  }

  /**
   * Run one FSM tick. If not yet initialized, enter() fires first then update()
   * runs on the same frame — matching the original StatefulEnemyRuntime behavior.
   */
  public tick(time: number, delta: number): void {
    if (!this.ready) {
      this.changedAt = time
      this.doEnter(this.buildCtx(time, delta, this.current, 0))
    }

    const stateTime = Math.max(0, time - this.changedAt)
    const ctx = this.buildCtx(time, delta, this.current, stateTime)
    this.lastCtx = ctx

    const next = this.getState().update(ctx)
    const t = next ?? this.pending
    if (t && t !== this.current) {
      this.pending = null
      this.doTransition(t, time, delta, ctx)
    }
  }

  public getStateName(): TState {
    return this.current
  }

  public getLastCtx(): TContext | null {
    return this.lastCtx
  }

  /** Queue a transition — executed at the end of the current tick. */
  public queueTransition(next: TState): void {
    this.pending = next
  }

  /** Alias for queueTransition() used by subclass-style runtimes. */
  public transitionTo(next: TState): void {
    this.queueTransition(next)
  }

  /** Force an immediate transition, calling exit() on the current state. */
  public forceTransition(next: TState, fromCtx?: TContext | null): void {
    const ctx = fromCtx ?? this.lastCtx
    this.doTransition(next, ctx?.time ?? 0, ctx?.delta ?? 0, ctx)
  }

  private doTransition(
    next: TState,
    time: number,
    delta: number,
    fromCtx?: TContext | null,
  ): void {
    if (fromCtx && this.ready) this.getState().exit?.(fromCtx)
    this.current = next
    this.changedAt = fromCtx?.time ?? time
    this.doEnter(this.buildCtx(time, delta, next, 0))
  }

  private doEnter(ctx: TContext): void {
    this.getState().enter?.(ctx)
    this.ready = true
    this.lastCtx = ctx
    this.onEntered(this.current, ctx)
    this.enteredCallback?.(this.current, ctx)
  }

  private getState(): RuntimeState<TState, TContext> {
    const s = this.stateMap.get(this.current)
    if (!s) throw new Error(`StatefulRuntime: unknown state "${this.current}"`)
    return s
  }

  protected onEntered(_state: TState, _ctx: TContext): void {}
}
